import { ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, isDevMode, InjectionToken, ErrorHandler, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { provideRouter, Router, withPreloading } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient } from '@angular/common/http';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideFunctions, getFunctions } from '@angular/fire/functions';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as Sentry from '@sentry/angular';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { provideServiceWorker } from '@angular/service-worker';
import { SelectivePreloadStrategy } from './core/preloading/selective-preload.strategy';
import { AuthService } from './services/auth.service';
import { effect } from '@angular/core';

// MD3 Dialog Data Token (replaces MAT_DIALOG_DATA)
export const MD3_DIALOG_DATA = new InjectionToken<any>('MD3_DIALOG_DATA');

// Custom TranslateLoader
export class TranslateHttpLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  public getTranslation(lang: string): Observable<any> {
    return this.http.get(`./assets/i18n/${lang}.json`);
  }
}

// Factory function for TranslateHttpLoader
export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http);
}

// ✅ AE-6: Sentry User Context Factory
// Sets user context in Sentry when auth state changes
export function initializeSentryUserContext(authService: AuthService): () => void {
  return () => {
    // Use effect to track user changes reactively
    effect(() => {
      const user = authService.currentUser();
      const profile = authService.userProfile();

      if (user && profile) {
        // Set user context for Sentry
        Sentry.setUser({
          id: user.uid,
          email: user.email || undefined,
          username: profile.displayName || profile.email,
          role: profile.role,
          center: profile.centerName || profile.centerId
        });
      } else {
        // Clear user context on logout
        Sentry.setUser(null);
      }
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    // ✅ LOCALE-FIX: Set default locale to Turkish
    // Fixes Bug 4.1: NG0701 Missing locale data error
    { provide: LOCALE_ID, useValue: 'tr' },

    // ✅ AE-6: Sentry Error Handler (Angular 20 Compatible Setup)
    // Using factory pattern to avoid injection context issues
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: true,
      }),
    },
    // ✅ AE-6: Sentry Tracing Service for performance monitoring
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    // ✅ AE-6: Initialize Sentry TraceService on app start
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    // ✅ AE-6: Initialize Sentry User Context tracking
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSentryUserContext,
      deps: [AuthService],
      multi: true,
    },
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // ⚡ OPTIMIZATION: Route preloading with custom strategy
    provideRouter(routes, withPreloading(SelectivePreloadStrategy)),
    provideAnimations(),
    provideHttpClient(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
    provideFunctions(() => getFunctions(getApp(), 'europe-west1')), provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000'
          })
  ]
};
